import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import MuiDrawer, { drawerClasses } from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MenuContent from './MenuContent';
import OptionsMenu from './OptionsMenu';
import { useAuth } from '../context/AuthContext';
import MenuButton from './MenuButton';

const drawerWidth = 240;

/** @param {string | null | undefined} value @param {number} maxLength */
function truncate(value, maxLength) {
  if (!value) return '';
  return value.length <= maxLength ? value : value.substr(0, maxLength) + '...';
}

function OptionsMenuIconButton({collapsed, toggle}) {
  return (
    <IconButton
      size={collapsed ? "small" : "medium"}
      onClick={toggle}
      sx={{
        ml: collapsed ? 0 : 1,
        mt: collapsed ? 0.5 : 0,
        p: collapsed ? 0.5 : 1,
      }}
    >
      {collapsed ? (
        <ChevronRightIcon fontSize={collapsed ? "small" : "inherit"} />
      ) : (
        <ChevronLeftIcon />
      )}
    </IconButton>
  );
}

const Drawer = styled(MuiDrawer)({
  width: drawerWidth,
  flexShrink: 0,
  boxSizing: 'border-box',
  mt: 10,
  [`& .${drawerClasses.paper}`]: {
    width: drawerWidth,
    boxSizing: 'border-box',
  },
});

import Label from '../../about/Label.jsx';

export default function SideMenu(props) {
    const { setPage } = props;
    const { user, isAdmin } = useAuth();
    const [aboutOpen, setAboutOpen] = useState(false);
    const [collapsed, setCollapsed] = useState(() => {
      try {
        const stored = localStorage.getItem('sideMenuCollapsed');
        return stored === 'true';
      } catch {
        return false;
      }
    });
    useEffect(() => {
      try {
        localStorage.setItem('sideMenuCollapsed', collapsed.toString());
      } catch (e) {
        // ignore
      }
    }, [collapsed]);

    const currentWidth = collapsed ? 70 : drawerWidth;

    const toggleCollapsed = () => {
      setCollapsed(prev => !prev);
    };

    return (
      <Drawer
        variant="permanent"
        sx={{
          width: currentWidth,
          flexShrink: 0,
          [`& .${drawerClasses.paper}`]: {
            width: currentWidth,
            backgroundColor: 'background.paper',
            overflowX: 'hidden',
            transition: (theme) => theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.standard,
            }),
          },
          display: { xs: 'none', md: 'block' },
        }}
      >
        <Stack
          direction={collapsed ? "column" : "row"}
          sx={{
            px: 2,
            py: 3,
            gap: collapsed ? 0.5 : 1.5,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box component="img" src="/images/fav_replico.png" alt="Replico" sx={{ width: collapsed ? 40 : 50, height: collapsed ? 40 : 50, borderRadius: '50%', objectFit: 'cover' }} />
          {!collapsed && (
            <Typography variant="h5" sx={{ fontWeight: 900, fontSize: '1.75rem' }}>
              Replico
            </Typography>
          )}
          <OptionsMenuIconButton collapsed={collapsed} toggle={toggleCollapsed} />
        </Stack>
        <Divider />
        <MenuContent setPage={setPage} isAdmin={isAdmin} collapsed={collapsed} onAboutClick={() => setAboutOpen(true)} aboutOpen={aboutOpen} />
        <Stack
          direction="row"
          sx={{
            p: 2,
            gap: 1,
            alignItems: 'center',
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          {collapsed ? (
            <OptionsMenu
              setPage={setPage}
              trigger={({ onClick }) => (
                <MenuButton
                  aria-label="Open profile menu"
                  onClick={onClick}
                  sx={{
                    borderColor: 'divider',
                    borderRadius: '50%',
                    p: 0.25,
                    borderRadius: '50%',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      borderRadius: '50%',
                    },
                  }}
                >
                  <Avatar
                    sizes="small"
                    alt={user?.displayName}
                    src={user?.photoURL}
                    sx={{ width: 36, height: 36 }}
                  />
                </MenuButton>
              )}
            />
          ) : (
            <Avatar
              sizes="small"
              alt={ user?.displayName }
              src={ user?.photoURL }
              sx={{ width: 36, height: 36 }}
            />
          )}
          <Box sx={{ mr: 'auto', display: collapsed ? 'none' : 'block' }}>
            <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: '16px' }}>
                {user && truncate(user.displayName, 15)}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {user && truncate(user.email, 18)}
            </Typography>
          </Box>
          {!collapsed && <OptionsMenu setPage={setPage}/>}
        </Stack>
        <Label open={aboutOpen} onClose={() => setAboutOpen(false)} showButton={false} />
      </Drawer>
    );
  }
  
